import React, { useState, useEffect } from "react";
import BlogCard from "./BlogCard/BlogCard";
import { subscribeMarketingNewsletter } from "../../../api/SubscribeNewsletter";
import { errorHandler } from "../../shared/Utils/ErrorHandler";
import { lineBreaks } from "../../shared/Utils/LineBreaks";
import SecondaryButton from "../../shared/Buttons/SecondaryButton";
import { getPaginatedBlogs } from "../../../api/FetchBlogs";
import Spinner from "../../shared/LoadingSpinner/Spinner";
import TextInput from "../../shared/Inputs/TextInput";
import { validateEmail } from "../../shared/Utils/ValidateEmail";
import PaginationButtons from "../../shared/Buttons/pagination";

function BlogSearch() {
  const [blogPosts, setBlogPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [filterParam, setFilterParam] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState({ error: "", success: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getAllBlogs = async (page, filterParam, searchParam = "") => {
    try {
      setIsLoading(true);
      const response = await getPaginatedBlogs(page, filterParam, searchParam);

      if (response) {
        setIsLoading(false);
        setBlogPosts(response.results);
        setTotalCount(response.count);
        // Calculate total pages (6 items per page based on backend)
        setTotalPages(Math.ceil(response.count / 6));
      }
    } catch (error) {
      console.error("Could not get blogs", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllBlogs(currentPage, filterParam);
  }, []);

  // Debounce search functionality
  useEffect(() => {
    if (searchTerm === "") {
      // If search is empty, just use the current filter
      getAllBlogs(1, filterParam);
      setCurrentPage(1);
      return;
    }

    const timer = setTimeout(() => {
      getAllBlogs(1, filterParam, searchTerm);
      setCurrentPage(1);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const changeCategory = (category) => {
    setActiveCategory(category);
    setFilterParam(category);
    getAllBlogs(1, category, searchTerm);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    getAllBlogs(newPage, filterParam, searchTerm);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const subscribeToNewsletter = async (email) => {
    const payload = {
      email: email,
      active: false,
    };

    // Validation for the email format
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      setError({ error: "", success: "" });
      return; // Stop further execution if validation fails
    }

    try {
      const response = await subscribeMarketingNewsletter(payload);
      setError({ error: "", success: "Successfully subscribed" });
      setEmail("");
      setTimeout(() => {
        setError({ error: "", success: "" });
      }, 2000);
    } catch (error) {
      let errorHandle = errorHandler(error);
      if (errorHandle.includes("email:")) {
        errorHandle = errorHandle.substring(7, errorHandle.length - 1);
      }

      const errorLine = lineBreaks(errorHandle);
      setError({ error: errorLine, success: "" });
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    subscribeToNewsletter(email);
  };

  return (
    <div className="mb-8 sm:grow">
      {/* Hero Section with Overlapping Images */}
      <div className="flex flex-col md:flex-row justify-between items-center w-full px-4 sm:px-8 lg:px-20 xl:px-32 py-6 lg:py-12 xl:py-20 mt-16 sm:mt-20 md:mt-24 overflow-hidden">
        <div className="max-w-[1360px] w-full mx-auto flex flex-col md:flex-row justify-between items-center gap-8 lg:gap-12">
          {/* Left side - Text content */}
          <div className="w-full md:w-1/2 text-center md:text-left order-1">
            <h1 className="text-blue text-3xl sm:text-4xl lg:text-5xl font-poppins font-medium mb-4 sm:mb-6 mt-10 sm-mt-0">
              The Daily Pets
            </h1>

            <p className="text-black text-base sm:text-lg font-montserrat font-semibold mb-4 max-w-[560px] mx-auto md:mx-0 leading-relaxed">
              Welcome to the Sponsor a Pet blog, your go to spot for
              heartwarming pet stories, helpful tips, and fun reads! Explore
              educational posts about our consulting services, discover our free
              resources, and stay inspired by everything we do to help animals
              find their forever homes.
            </p>

            <p className="text-black text-base sm:text-lg font-montserrat font-semibold mb-4 max-w-[560px] mx-auto md:mx-0 leading-relaxed">
              Subscribe to get the best pet stories in your inbox:
            </p>

            {/* Email subscription */}
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto md:mx-0 mb-4"
            >
              <input
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter Email"
                className="flex-1 px-3 py-2 border border-[#7C7C7C] rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm placeholder-gray-400 text-left h-10"
                required
              />
              <button
                type="submit"
                className="bg-orange hover:bg-orange-dark1 text-white shadow-lg text-sm font-semibold py-2 px-4 sm:px-6 rounded-full transition-all duration-300 whitespace-nowrap h-10"
                style={{ boxShadow: "0px 2px 3.6px 0px rgba(0, 0, 0, 0.43)" }}
              >
                Subscribe
              </button>
            </form>

            {emailError && (
              <p className="text-red-500 text-sm mt-2 text-center md:text-left">
                {emailError}
              </p>
            )}
            {error.error && (
              <p className="text-red-500 text-sm mt-2 text-center md:text-left">
                {error.error}
              </p>
            )}
            {error.success && (
              <p className="text-green-500 text-sm mt-2 text-center md:text-left">
                {error.success}
              </p>
            )}
          </div>

          {/* Right side - Overlapping images */}

          {/* Smaller version for screens below xl */}
          <div className="w-full md:w-1/2 h-[280px] sm:h-[320px] lg:h-[360px] relative justify-center md:justify-end order-2 hidden md:flex xl:hidden">
            {/* First image - compact positioning */}
            <div className="absolute z-10 top-0 left-1/2 transform -translate-x-1/2 translate-x-8 md:left-auto md:right-24 lg:right-28 md:transform-none">
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/blogs-page/1.png"
                alt="Pet story"
                className="w-[160px] sm:w-[200px] lg:w-[240px] h-auto max-h-[110px] sm:max-h-[140px] lg:max-h-[160px] object-cover rounded-xl shadow-lg"
              />
            </div>

            {/* Second image - compact positioning */}
            <div className="absolute top-24 sm:top-24 lg:top-32 left-1/2 transform -translate-x-1/2 -translate-x-8 md:left-auto md:right-0 md:transform-none z-20">
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/blogs-page/2.png"
                alt="Happy pets"
                className="w-[160px] sm:w-[200px] lg:w-[240px] h-auto max-h-[110px] sm:max-h-[140px] lg:max-h-[160px] object-cover rounded-xl shadow-xl"
              />
            </div>

            {/* Pawprint - compact size */}
            <div className="absolute -top-1 sm:-top-2 left-1/2 transform -translate-x-1/2 translate-x-16 md:left-auto md:-right-2 sm:md:-right-4 md:transform-none w-[60px] sm:w-[80px] lg:w-[100px] h-[60px] sm:h-[80px] lg:h-[100px] z-30">
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/blogs-page/paw1.png"
                alt=""
                className="w-full h-full object-contain"
              />
            </div>

            {/* Pawprint - compact positioning */}
            <div className="absolute bottom-12 sm:bottom-24 lg:bottom-20 left-1/2 transform -translate-x-1/2 -translate-x-16 md:left-auto md:right-60 lg:right-72 md:transform-none w-[50px] sm:w-[70px] lg:w-[90px] h-[50px] sm:h-[70px] lg:h-[90px] z-30">
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/blogs-page/paw2.png"
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Larger version for xl screens and above */}
          <div className="w-full md:w-1/2 h-[500px] relative flex justify-center md:justify-end order-2 hidden xl:flex">
            {/* First image - top left */}
            <div className="absolute z-10 top-0 right-52">
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/blogs-page/1.png"
                alt="Pet story"
                className="w-[344px] h-auto max-h-[231px] object-cover rounded-xl shadow-lg"
              />
            </div>

            {/* Second image - bottom right, overlapping */}
            <div className="absolute top-44 right-0 z-20">
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/blogs-page/2.png"
                alt="Happy pets"
                className="w-[344px] h-auto max-h-[231px] object-cover rounded-xl shadow-xl"
              />
            </div>

            {/* Pawprint - top right corner */}
            <div className="absolute -top-5 -right-10 w-[200px] h-[200px] z-30">
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/blogs-page/paw1.png"
                alt=""
                className="w-full h-full object-contain"
              />
            </div>

            {/* Pawprint - bottom left corner */}
            <div className="absolute bottom-28 right-[400px] w-[140px] h-[140px] z-30">
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/blogs-page/paw2.png"
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="w-full px-4 sm:px-8 lg:px-20 xl:px-32 mb-8 sm:mb-12">
        <div className="max-w-[1360px] w-full mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 lg:gap-6">
            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2 sm:gap-4 justify-center md:justify-start">
              <button
                onClick={() => changeCategory("all")}
                className={`px-4 sm:px-6 py-2 rounded-full font-medium text-sm transition-all duration-300 ${
                  activeCategory === "all"
                    ? "text-white bg-[#1B3A5D] hover:opacity-80"
                    : "text-[#1B3A5D] bg-white border border-[#1B3A5D] hover:bg-[#1B3A5D] hover:text-white"
                }`}
                style={{ boxShadow: "0px 2px 3.6px 0px rgba(0, 0, 0, 0.43)" }}
              >
                All
              </button>
              <button
                onClick={() => changeCategory("educational")}
                className={`px-4 sm:px-6 py-2 rounded-full font-medium text-sm transition-all duration-300 ${
                  activeCategory === "educational"
                    ? "text-white bg-[#1B3A5D] hover:opacity-80"
                    : "text-[#1B3A5D] bg-white border border-[#1B3A5D] hover:bg-[#1B3A5D] hover:text-white"
                }`}
                style={{ boxShadow: "0px 2px 3.6px 0px rgba(0, 0, 0, 0.43)" }}
              >
                Educational
              </button>
              <button
                onClick={() => changeCategory("fun")}
                className={`px-4 sm:px-6 py-2 rounded-full font-medium text-sm transition-all duration-300 ${
                  activeCategory === "fun"
                    ? "text-white bg-[#1B3A5D] hover:opacity-80"
                    : "text-[#1B3A5D] bg-white border border-[#1B3A5D] hover:bg-[#1B3A5D] hover:text-white"
                }`}
                style={{ boxShadow: "0px 2px 3.6px 0px rgba(0, 0, 0, 0.43)" }}
              >
                Fun
              </button>
            </div>

            {/* Search Input */}
            <div className="relative w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full sm:w-80 px-3 pl-10 py-2 border border-[#7C7C7C] rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm placeholder-gray-400 text-left h-10"
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          {/* Horizontal line separator */}
          <div className="w-full h-0.5 bg-gray-300 mt-4"></div>
        </div>
      </div>

      <div className="flex flex-col items-center pt-2">
        {isLoading ? (
          <div className="flex flex-row mx-auto mt-16 w-full pb-24 justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="w-full px-4 sm:px-8 lg:px-20 xl:px-32">
              <div className="max-w-[1360px] mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-12 mb-8 justify-items-center md:justify-items-start">
                  {blogPosts.map((blog, index) => (
                    <BlogCard key={index + blog.title} {...blog} />
                  ))}
                </div>
              </div>
            </div>

            {/* Pagination Buttons */}
            <PaginationButtons
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default BlogSearch;
